import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { getItemBuyers, updateAdminItem, getFilterTable, deleteAdminItem } from '../../api/recommendationAPI';
import '../Profile.css';
import { ADMIN_EMAIL } from '../../constants';

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('he-IL') : '—';

const BuyersPopup = ({ itemId, onClose }) => {
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getItemBuyers(itemId)
      .then(data => setBuyers(Array.isArray(data) ? data : []))
      .catch(() => setError('שגיאה בטעינת קונים'))
      .finally(() => setLoading(false));
  }, [itemId]);

  return (
    <div className="rec-popup-overlay" onClick={onClose}>
      <div className="user-details-popup" onClick={e => e.stopPropagation()}>
        <button className="rec-popup-close" onClick={onClose}>✕</button>
        <div className="user-details-name" style={{ marginBottom: 16 }}>רשימת קונים</div>
        {loading && <div className="history-status">טוען...</div>}
        {error && <div className="history-status" style={{ color: 'red' }}>{error}</div>}
        {!loading && !error && buyers.length === 0 && <div className="user-details-empty">אין קונים עדיין</div>}
        {!loading && !error && buyers.length > 0 && (
          <div className="users-table-wrapper" style={{ maxHeight: '55vh' }}>
            <table className="users-table">
              <thead>
                <tr><th>שם</th><th>מייל</th><th>תאריך</th><th>הזמנה</th><th>מחיר</th></tr>
              </thead>
              <tbody>
                {buyers.map((b, idx) => (
                  <tr key={b.id || idx}>
                    <td>{b.name}</td>
                    <td className="users-td-email">{b.email}</td>
                    <td>{formatDate(b.purchase_date)}</td>
                    <td className="users-td-num">#{b.order_id}</td>
                    <td>₪{Number(b.price).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// בוחר תגיות מרשימה — מציג נבחרות עם X, ו-dropdown לבחירה
const TagSelector = ({ label, allOptions, selected, onToggle }) => {
  const selectedIds = new Set(selected.map(s => s.id));
  const available = allOptions.filter(o => !selectedIds.has(o.id));

  return (
    <div className="prog-tag-editor">
      <div className="prog-detail-label">{label}</div>
      <div className="prog-tag-list">
        {selected.map(item => (
          <span key={item.id} className="prog-tag prog-tag-removable">
            {item.name}
            <button className="prog-tag-remove" onClick={() => onToggle(item, false)}>✕</button>
          </span>
        ))}
      </div>
      {available.length > 0 && (
        <select
          className="rec-popup-input prog-tag-select"
          value=""
          onChange={e => {
            const opt = allOptions.find(o => o.id === Number(e.target.value));
            if (opt) onToggle(opt, true);
          }}
          dir="rtl"
        >
          <option value="">הוסף {label}...</option>
          {available.map(o => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
      )}
    </div>
  );
};

const initForm = (item) => ({
  name: item.name || '',
  description: item.description || '',
  short_description: item.short_description || '',
  price: item.price ?? '',
  sale_price: item.sale_price ?? '',
  sale_deadline: item.sale_deadline ? item.sale_deadline.slice(0, 16) : '',
  genre: item.genre || '',
  link: item.link || '',
  url: item.url || '',
  video: item.video || false,
  has_coupon: item.has_coupon || false,
  much_for: item.much_for || '',
  crowds: item.crowds ? [...item.crowds] : [],
  timings: item.timings ? [...item.timings] : [],
  types: item.types ? [...item.types] : [],
  files_keep: (item.files || []).map(f => f.id),
  files_add: [],
  _files: item.files ? [...item.files] : [],
});

const AdminProgramDetail = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const [item] = useState(location.state?.item || null);
  const [showBuyers, setShowBuyers] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [form, setForm] = useState(() => item ? initForm(item) : {});

  // רשימות מלאות מה-API
  const [allCrowds, setAllCrowds] = useState([]);
  const [allTimings, setAllTimings] = useState([]);
  const [allTypes, setAllTypes] = useState([]);

  // קבצים חדשים
  const [newFileUrl, setNewFileUrl] = useState('');
  const [newFileType, setNewFileType] = useState('image');
  const [newFileForShow, setNewFileForShow] = useState(true);

  useEffect(() => {
    if (!user || user.email !== ADMIN_EMAIL) { navigate('/', { replace: true }); return; }
    // שליפת רשימות הפילטרים
    Promise.all([
      getFilterTable('crowds'),
      getFilterTable('timings'),
      getFilterTable('types'),
    ]).then(([crowds, timings, types]) => {
      setAllCrowds(Array.isArray(crowds) ? crowds : []);
      setAllTimings(Array.isArray(timings) ? timings : []);
      setAllTypes(Array.isArray(types) ? types : []);
    });
  }, [user, navigate]);

  if (!user || user.email !== ADMIN_EMAIL) return null;
  if (!item) return (
    <div className="history-page">
      <div className="history-status">
        לא נמצאו נתונים.{' '}
        <button className="admin-rec-cancel" onClick={() => navigate('/profile/admin-programs')}>חזרה</button>
      </div>
    </div>
  );

  const setF = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const toggleTag = (key, item, add) =>
    setForm(f => ({
      ...f,
      [key]: add ? [...f[key], item] : f[key].filter(t => t.id !== item.id),
    }));

  const removeFile = (fileId) =>
    setForm(f => ({
      ...f,
      files_keep: f.files_keep.filter(fid => fid !== fileId),
      _files: f._files.filter(fi => fi.id !== fileId),
    }));

  const addFile = () => {
    if (!newFileUrl.trim()) return;
    setForm(f => ({
      ...f,
      files_add: [...f.files_add, { url: newFileUrl.trim(), type: newFileType, for_show: newFileForShow }],
    }));
    setNewFileUrl('');
  };

  const removeNewFile = (idx) =>
    setForm(f => ({ ...f, files_add: f.files_add.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      await updateAdminItem(id, {
        name: form.name,
        description: form.description,
        short_description: form.short_description,
        price: Number(form.price),
        sale_price: form.sale_price !== '' && form.sale_price != null ? Number(form.sale_price) : null,
        sale_deadline: form.sale_deadline ? new Date(form.sale_deadline).toISOString() : null,
        genre: form.genre,
        link: form.link,
        url: form.url,
        video: form.video,
        has_coupon: form.has_coupon,
        much_for: form.much_for,
        crowd_ids: form.crowds.map(c => c.id),
        timing_ids: form.timings.map(t => t.id),
        type_ids: form.types.map(t => t.id),
        files_keep: form.files_keep,
        files_add: form.files_add,
      });
      setSaveSuccess(true);
      setEditing(false);
    } catch {
      setSaveError('שגיאה בשמירה, נסי שוב');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setForm(initForm(item));
    setNewFileUrl('');
    setSaveError(null);
    setEditing(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteAdminItem(id);
      navigate('/profile/admin-programs', { replace: true });
    } catch {
      setSaveError('שגיאה במחיקה, נסי שוב');
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  const GENRE_OPTIONS = ['מיניטס', 'תוכנית', 'מיתוג'];

  const field = (label, key, type = 'text') => (
    <div className="prog-detail-field">
      <div className="prog-detail-label">{label}</div>
      {editing ? (
        type === 'textarea' ? (
          <textarea className="rec-popup-textarea" value={form[key]} onChange={e => setF(key, e.target.value)} rows={3} dir="rtl" />
        ) : type === 'checkbox' ? (
          <input type="checkbox" checked={!!form[key]} onChange={e => setF(key, e.target.checked)} />
        ) : (
          <input className="rec-popup-input" type={type} value={form[key] ?? ''} onChange={e => setF(key, e.target.value)} dir="rtl" />
        )
      ) : (
        <div className="prog-detail-value">
          {type === 'checkbox' ? (form[key] ? 'כן' : 'לא') : (form[key] || '—')}
        </div>
      )}
    </div>
  );

  return (
    <div className="history-page">
      <div className="prog-detail-topbar">
        <button className="admin-rec-cancel" onClick={() => navigate('/profile/admin-programs')}>← חזרה</button>
        <div className="prog-detail-title">{item.name}</div>
        <div className="prog-detail-actions">
          <button className="users-page-btn" onClick={() => setShowBuyers(true)}>
            קונים ({item.buyers_count || 0})
          </button>
          {editing ? (
            <>
              <button className="rec-popup-submit" onClick={handleSave} disabled={saving}>
                {saving ? 'שומר...' : 'שמור'}
              </button>
              <button className="admin-rec-cancel" onClick={cancelEdit}>ביטול</button>
            </>
          ) : (
            <>
              <button className="admin-rec-edit-btn" onClick={() => setEditing(true)}>עריכה</button>
              {confirmDelete ? (
                <>
                  <span className="prog-delete-confirm-text">בטוחה?</span>
                  <button className="prog-delete-confirm-btn" onClick={handleDelete} disabled={deleting}>
                    {deleting ? '...' : 'כן, מחקי'}
                  </button>
                  <button className="admin-rec-cancel" onClick={() => setConfirmDelete(false)}>לא</button>
                </>
              ) : (
                <button className="prog-delete-btn" onClick={() => setConfirmDelete(true)}>מחיקה</button>
              )}
            </>
          )}
        </div>
      </div>

      {saveError && <div className="history-status" style={{ color: 'red' }}>{saveError}</div>}
      {saveSuccess && <div className="history-status" style={{ color: 'green' }}>נשמר בהצלחה ✓</div>}

      <div className="prog-detail-grid">
        {field('שם', 'name')}
        <div className="prog-detail-field">
          <div className="prog-detail-label">סוג</div>
          {editing ? (
            <select
              className="rec-popup-input"
              value={form.genre}
              onChange={e => setF('genre', e.target.value)}
              dir="rtl"
            >
              <option value="">בחר סוג...</option>
              {GENRE_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          ) : (
            <div className="prog-detail-value">{form.genre || '—'}</div>
          )}
        </div>
        {field('מחיר', 'price', 'number')}
        {field('מחיר מבצע', 'sale_price', 'number')}
        <div className="prog-detail-field">
          <div className="prog-detail-label">דד ליין מבצע</div>
          {editing ? (
            <input
              className="rec-popup-input"
              type="datetime-local"
              value={form.sale_deadline || ''}
              onChange={e => setF('sale_deadline', e.target.value)}
            />
          ) : (
            <div className="prog-detail-value">
              {form.sale_deadline ? new Date(form.sale_deadline).toLocaleString('he-IL') : '—'}
            </div>
          )}
        </div>
        {field('לינק', 'link')}
        {field('URL', 'url')}
        {field('וידאו', 'video', 'checkbox')}
        {field('קופון', 'has_coupon', 'checkbox')}
        {field('מתאים ל', 'much_for')}
        {field('תיאור קצר', 'short_description', 'textarea')}
        {field('תיאור מלא', 'description', 'textarea')}
      </div>

      {/* תגיות */}
      <div className="prog-detail-tags-section">
        {editing ? (
          <>
            <TagSelector
              label="קהלים"
              allOptions={allCrowds}
              selected={form.crowds}
              onToggle={(item, add) => toggleTag('crowds', item, add)}
            />
            <TagSelector
              label="תזמונים"
              allOptions={allTimings}
              selected={form.timings}
              onToggle={(item, add) => toggleTag('timings', item, add)}
            />
            <TagSelector
              label="סוגים"
              allOptions={allTypes}
              selected={form.types}
              onToggle={(item, add) => toggleTag('types', item, add)}
            />
          </>
        ) : (
          <>
            {form.crowds?.length > 0 && (
              <div className="prog-detail-tag-group">
                <span className="prog-detail-label">קהלים:</span>
                {form.crowds.map(c => <span key={c.id} className="prog-tag">{c.name}</span>)}
              </div>
            )}
            {form.timings?.length > 0 && (
              <div className="prog-detail-tag-group">
                <span className="prog-detail-label">תזמונים:</span>
                {form.timings.map(t => <span key={t.id} className="prog-tag">{t.name}</span>)}
              </div>
            )}
            {form.types?.length > 0 && (
              <div className="prog-detail-tag-group">
                <span className="prog-detail-label">סוגים:</span>
                {form.types.map(t => <span key={t.id} className="prog-tag">{t.name}</span>)}
              </div>
            )}
          </>
        )}
      </div>

      {/* קבצים */}
      <div className="prog-detail-files-section">
        <div className="prog-detail-label" style={{ marginBottom: 8 }}>קבצים</div>
        <div className="prog-detail-files">
          {form._files.map(f => (
            <div key={f.id} className="prog-file-item">
              {f.type === 'image'
                ? <img src={f.url} alt="" className="prog-detail-img" />
                : <a href={f.url} target="_blank" rel="noreferrer" className="prog-file-link">{f.url}</a>
              }
              {editing && <button className="prog-file-remove" onClick={() => removeFile(f.id)}>✕</button>}
            </div>
          ))}
          {form.files_add.map((f, idx) => (
            <div key={idx} className="prog-file-item prog-file-new">
              {f.type === 'image'
                ? <img src={f.url} alt="" className="prog-detail-img" />
                : <a href={f.url} target="_blank" rel="noreferrer" className="prog-file-link">{f.url}</a>
              }
              <button className="prog-file-remove" onClick={() => removeNewFile(idx)}>✕</button>
            </div>
          ))}
        </div>
        {editing && (
          <div className="prog-file-add-row">
            <input
              className="rec-popup-input"
              placeholder="URL של קובץ..."
              value={newFileUrl}
              onChange={e => setNewFileUrl(e.target.value)}
              dir="ltr"
            />
            <select
              className="rec-popup-input prog-file-type-select"
              value={newFileType}
              onChange={e => setNewFileType(e.target.value)}
            >
              <option value="image">תמונה</option>
              <option value="video">וידאו</option>
              <option value="file">קובץ</option>
            </select>
            <label className="prog-file-forshow">
              <input type="checkbox" checked={newFileForShow} onChange={e => setNewFileForShow(e.target.checked)} />
              להצגה
            </label>
            <button className="prog-tag-add-btn" onClick={addFile}>+</button>
          </div>
        )}
      </div>

      {showBuyers && <BuyersPopup itemId={id} onClose={() => setShowBuyers(false)} />}
    </div>
  );
};

export default AdminProgramDetail;
